<template>
  <page-list
    :list="list"
    :columns="columns"
    :export-data-options="exportDataOptions"
    :single-actions="singleActions" />
</template>

<script>
import * as R from 'ramda'
import WindowsMixin from '@/mixins/windows'
import ListMixin from '@/mixins/list'
import {
  getNameFilter,
  getTimeRangeFilter,
  getDistinctFieldFilter,
} from '@/utils/common/tableFilter'
import { getTimeTableColumn, getCopyWithContentTableColumn } from '@/utils/common/tableColumn'
import expectStatus from '@/constants/expectStatus'

export default {
  name: 'MonthReportRecordsList',
  mixins: [WindowsMixin, ListMixin],
  props: {
    id: String,
    getParams: [Object, Function],
  },
  data () {
    return {
      list: this.$list.createList(this, {
        id: this.id,
        resource: 'emailqueues',
        apiVersion: 'v2',
        getParams: this.getParam,
        filterOptions: {
          id: {
            field: 'id',
            label: 'ID',
          },
          subject: getNameFilter({ field: 'subject', label: this.$t('report.email_subject') }),
          dest: getNameFilter({ field: 'dest', label: this.$t('report.send_emails') }),
          recv_at: getTimeRangeFilter({ label: this.$t('report.send_time'), field: 'recv_at' }),
          roles: getDistinctFieldFilter({ label: this.$t('report.notification_role'), field: 'roles' }),
        },
        hiddenColumns: [],
        steadyStatus: Object.values(expectStatus.reportRecord).flat(),
      }),
      columns: [
        getCopyWithContentTableColumn({ field: 'id', title: '#ID' }),
        getTimeTableColumn({ field: 'recv_at', title: this.$t('report.send_time') }),
        {
          field: 'status',
          title: this.$t('common.status'),
          slots: {
            default: ({ row }) => {
              const btn = <a-button type='link' onclick={() => this.showErrorLog(row)}>{this.$t('common.view')}</a-button>
              return [
                <div class='d-flex align-items-center text-truncate'>
                  <status status={row.status} statusModule='reportRecord' />
                  {row.status === 'fail' && row.results ? btn : null}
                </div>,
              ]
            },
          },
        },
        {
          field: 'roles',
          title: this.$t('report.notification_role'),
          formatter: ({ row }) => {
            return row.more_details?.receiver_role || '-'
          },
        },
        {
          field: 'dest',
          title: this.$t('report.send_emails'),
        },
        {
          field: 'subject',
          title: this.$t('report.email_subject'),
        },
        {
          field: 'project_domain',
          title: this.$t('res.project'),
          slots: {
            default: ({ row }) => {
              const ret = []
              const project = row.more_details?.project
              const domain = row.more_details?.domain
              if (project) {
                ret.push(
                  <list-body-cell-wrap copy hide-field field="project" row={{ project }}>
                    {project}
                  </list-body-cell-wrap>,
                )
              }
              if (domain) {
                ret.push(
                  <list-body-cell-wrap hide-field copy field="domain" row={{ domain }}>
                    <span class='text-weak'>{domain}</span>
                  </list-body-cell-wrap>,
                )
              }
              return ret.length ? ret : '-'
            },
          },
        },
      ],
      singleActions: [
        {
          label: this.$t('report.report_preview'),
          action: (obj) => {
            if (obj.content && obj.content.body) {
              this.showPreview(obj)
            } else if (obj.content && obj.content.attachments && obj.content.attachments.length && obj.content.attachments[0].mime === 'application/pdf') {
              this.showPdfPreview(obj)
            }
          },
          meta: (obj) => {
            const ret = { validate: false }
            if (obj.content && obj.content.body) {
              ret.validate = true
            }
            if (obj.content && obj.content.attachments && obj.content.attachments.length && obj.content.attachments[0].mime === 'application/pdf') {
              ret.validate = true
            }
            return ret
          },
        },
      ],
    }
  },
  computed: {
    exportDataOptions () {
      return {
        downloadType: 'local',
        title: this.$t('report.report_subscription'),
        items: this.columns,
      }
    },
  },
  created () {
    this.fetchData()
  },
  methods: {
    getParam () {
      const getParams = (R.is(Function, this.getParams) ? this.getParams() : this.getParams)
      return {
        scope: this.$store.getters.scope,
        details: false,
        ...getParams,
      }
    },
    fetchData () {
      this.list.fetchData()
    },
    showErrorLog (data) {
      this.createDialog('ReportSendResultDialog', {
        data,
      })
    },
    showPreview (data) {
      this.createDialog('ReportPreviewDialog', {
        data,
      })
    },
    showPdfPreview (data) {
      const attachment = data.content?.attachments?.[0]
      if (!attachment || !attachment.content) {
        this.$message.warning(this.$t('report.no_preview_content'))
        return
      }
      this.createDialog('ReportPdfPreviewDialog', {
        data: {
          pdfContent: attachment.content,
          filename: attachment.filename || 'report.pdf',
        },
      })
    },
    handleOpenSidepage (row) {
      this.sidePageTriggerHandle(this, 'MonthReportSidePage', {
        id: row.id,
        resource: () => {
          return {
            data: row,
          }
        },
      }, {
        list: this.list,
      })
    },
  },
}
</script>
